import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useUserAndGameContext } from '../../../../contexts/UserAndGameContext';
import { useSuperPickemContext } from '../../context/SuperPickemContext';
import LoadingScreen from '../../../LoadingScreen/LoadingScreen';
import UserSubmitBar from '../../../GamePageComponents/SubmitBars/UserSubmitBar';
import { useSaveBracketPicks } from '../../../../hooks/pools/useSaveBracketPicks';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import isEqual from 'lodash/isEqual';
import { DateTime } from 'luxon';
import SuperPickemBracketSubmissionModal from '../../../GameModals/BracketGameModals/SuperPickemBracketSubmissionalModal';
import AgnosticDesktopChamp from '../../../Brackets/SixteenTeamBracket/AgnosticDesktopChamp';
import { useDispatch } from 'react-redux';
import { userLogoutAction } from '../../../../redux/actions/loginImport';
import AgnosticSixteenTeamBracket from '../../../Brackets/SixteenTeamBracket/AgnosticSixteenTeamBracketDesktop';
import useNavigateToTop from '../../../../hooks/general/useNavigateToTop';
import { useSaveSuperPickemBracketPicks } from '../../../../hooks/pools/useSaveSuperPickemBracket';
import QuickPickGamesComing from '../../../QuickPickGames/CardPieces/QuickPickGamesComing';
import CountdownClock from '../../../GameHomePage/GameHomePageComponents/CountdownClock';

const SixteenTeamDesktopBracket = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery("(max-width:900px)");

    const {
        tournamentSchedule,
        teams,
        usingSeriesLengths,
        picksInfo,
        lastRound,
        hasBracketDeadlinePassed,
        selectedUserBracketInfo,
    } = useSuperPickemContext();

    const {
        myUserId,
        masterPoolId,
        pickIndex,
        masterPoolData,
        league,
        pickState,
        originalPicks,
        setPickState,
        tiebreaker,
        setTiebreaker,
        originalTiebreaker,
        gameData,
        bracketState,
        setBracketState,
        originalBracketPicks,
        seriesLengths,
        setSeriesLengths,
        originalSeriesLengths,
        setUnsavedPicks,
        gameAddress,
        gameId,
        isUserInThisPool,
        firstGameTime,
        revealTime,
        page
    } = useUserAndGameContext();

    

    const dispatch = useDispatch();
    const navigateToTop = useNavigateToTop();

    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [regions, setRegions] = useState(null);
    const [bracket, setBracket] = useState(null);
    const [submitBarOpen, setSubmitBarOpen] = useState(false);
    const [regionCompleteModalOpen, setRegionCompleteModalOpen] = useState(false);
    const [preBracketModalOpen, setPreBracketModalOpen] = useState(false);
    const [submissionModalOpen, setSubmissionModalOpen] = useState(false);
    const [submissionData, setSubmissionData] = useState(null);

    const myBracketModalInfo = masterPoolData?.modalInfo?.myBracket;

    const timeNowInNewYork = DateTime.now().setZone("America/New_York");
    const revealTimeInNewYork = DateTime.fromISO(revealTime, { zone: "America/New_York" });
    const firstGameTimeInNewYork = DateTime.fromISO(firstGameTime, { zone: "America/New_York" });

    const revealTimePassed = timeNowInNewYork > revealTimeInNewYork;
    const firstGameTimePassed = timeNowInNewYork > firstGameTimeInNewYork;

    const countdownInfo = gameData?.countdownInfo || masterPoolData?.countdownInfo;
    const countdownText = (revealTime && !revealTimePassed) ? countdownInfo?.revealText : (firstGameTime && !firstGameTimePassed) ? (isUserInThisPool ? countdownInfo?.countdownTextInGame : countdownInfo?.countdownTextNotInGame) : countdownInfo?.intervalCountdown ? countdownInfo?.intervalText : null;
    const countdownTime = (revealTime && !revealTimePassed) ? revealTimeInNewYork : (firstGameTime && !firstGameTimePassed) ? firstGameTimeInNewYork : null;

    // Initialize roundDash from URL or default to 1
    const [roundDash, setRoundDash] = useState(() => {
        const navParam = searchParams.get('bracketNav');
        return navParam ? parseInt(navParam, 10) : 1;
    });

    const { mutate: saveSuperPickemBracketPicks } = useSaveSuperPickemBracketPicks();  

    // Use Effect to see if originalPicks is different from pickState
    // And if originalTiebreaker is different from tiebreaker
    // If so setSubmitBarOpen to true, otherwise false
    useEffect(() => {
        if (originalBracketPicks && bracketState) {
            const picksAreEqual = 
                originalBracketPicks?.length === bracketState?.length &&
                originalBracketPicks?.every(originalPick =>
                    bracketState?.some(currentPick => isEqual(originalPick, currentPick))
                );
                
            let seriesLengthsAreEqual = true;
            if (usingSeriesLengths && seriesLengths && originalSeriesLengths) {
                seriesLengthsAreEqual = 
                    originalSeriesLengths?.length === seriesLengths?.length &&
                    originalSeriesLengths?.every(originalLength =>
                        seriesLengths?.some(currentLength => isEqual(originalLength, currentLength))
                    );
            }
            
            // Set submit bar to open if either picks or tiebreakers are different
            setSubmitBarOpen(!picksAreEqual || !seriesLengthsAreEqual);
            setUnsavedPicks(!picksAreEqual || !seriesLengthsAreEqual);
        }
    }, [originalBracketPicks, bracketState, originalSeriesLengths, seriesLengths, usingSeriesLengths, setUnsavedPicks]);

    const effectiveBracketState = useMemo(() => {
        return page === 'my-bracket' 
            ? bracketState 
            : (selectedUserBracketInfo?.bracketPicks || []);
    }, [page, bracketState, selectedUserBracketInfo?.bracketPicks]);
        
    const effectiveSeriesLengths = useMemo(() => {
        return page === 'my-bracket' 
            ? seriesLengths 
            : (selectedUserBracketInfo?.seriesLengths || []);
    }, [page, seriesLengths, selectedUserBracketInfo?.seriesLengths]);

    const effectiveBracketStateLength = effectiveBracketState?.length || 0;

    // Group points by desktop bracket layout (sides to middle)
    function memoizePointsByDesktopLayout(bracketState, seriesLengths) {
        // Define the groups based on matchupIds
        const matchupGroups = [
            { groupName: 'leftRound1', matchupIds: [1, 2, 3, 4] },
            { groupName: 'leftRound2', matchupIds: [9, 10] },
            { groupName: 'leftFinal', matchupIds: [13] },
            { groupName: 'championship', matchupIds: [15] },
            { groupName: 'rightFinal', matchupIds: [14] },
            { groupName: 'rightRound2', matchupIds: [11, 12] },
            { groupName: 'rightRound1', matchupIds: [5, 6, 7, 8] }
        ];
        
        // Initialize the result array with all groups set to 0 points
        const result = matchupGroups.map(group => ({
            groupName: group.groupName,
            matchupIds: group.matchupIds,
            points: 0
        }));
        
        // Process bracketState points
        bracketState.forEach(item => {
            const matchupId = item.matchupId;
            // Find which group this matchup belongs to
            const groupIndex = result.findIndex(group => 
                group.matchupIds.includes(matchupId)
            );
            
            if (groupIndex !== -1 && item.points !== undefined) {
                result[groupIndex].points += Number(item.points); // Convert to number
            }
        });
        
        // Process seriesLengths points
        seriesLengths.forEach(item => {
            const matchupId = item.matchupId;
            // Find which group this matchup belongs to
            const groupIndex = result.findIndex(group => 
                group.matchupIds.includes(matchupId)
            );
            
            if (groupIndex !== -1 && item.points !== undefined) {
                result[groupIndex].points += Number(item.points); // Convert to number
            }
        });
        
        return result;
    }

    // Usage with useMemo
    const desktopPointsLayout = useMemo(() => {
                
        return memoizePointsByDesktopLayout(effectiveBracketState, effectiveSeriesLengths);
    }, [effectiveBracketState, effectiveSeriesLengths]);
    //console.log("Points by Round: ", desktopPointsLayout, bracketState, seriesLengths);

  

    const tournamentGames = tournamentSchedule?.Games;
    //console.log("Tournament Games: ", tournamentGames, "Tournament Schedule: ", tournamentSchedule);
    

    const picksToMake = gameData?.gameParameters?.picksToMake ?? masterPoolData?.gameParameters?.picksToMake;
    const tiebreakerCount = gameData?.gameParameters?.tiebreakerCount ?? masterPoolData?.gameParameters?.tiebreakerCount;

    const picksThisRegion = pickState?.filter(pick => pick.bracket === bracket)?.length;
    const gamesThisRegion = tournamentGames?.filter(game => game.Bracket === bracket)?.length;

    const regionInfo = masterPoolData?.gameParameters?.regionInfo;

    const handleGameClick = (teamId, matchupId, round, seedNumber) => {
        //console.log("Clicked game: ", teamId, matchupId, round, seedNumber);

        let newBracketPicks = [...bracketState];

        const existingPickIndex = newBracketPicks.findIndex(pick =>
            pick.matchupId === matchupId && pick.round === round
        );

        if (existingPickIndex !== -1 && newBracketPicks[existingPickIndex].teamId === teamId) {
            return; // No change if the same team is clicked again
        }

        const newPick = {
            teamId,
            round,
            matchupId,
            teamSeed: seedNumber
        };

        let finalPicks;
        if (existingPickIndex !== -1) {
            // Get the team that's being replaced
            const teamBeingReplaced = newBracketPicks[existingPickIndex].teamId;

            // First remove the existing pick from that position (to avoid duplicates)
            newBracketPicks.splice(existingPickIndex, 1);

            // Then filter out any other instances of the team being replaced in later rounds
            finalPicks = newBracketPicks.filter(pick =>
                pick.round < round || pick.teamId !== teamBeingReplaced
            );

            // Now add the new pick
            finalPicks.push(newPick);

            // Sort to maintain order
            finalPicks.sort((a, b) => {
                if (a.round === b.round) {
                    return a.teamSeed - b.teamSeed;
                }
                return a.round - b.round;
            });

            setBracketState(finalPicks);
            
        } else {
            finalPicks = [...newBracketPicks, newPick];

            setBracketState(finalPicks);
        }
        
        //console.log('Final picks before setting state:', finalPicks);
    
        setPickState(finalPicks);
    };


    const handleBracketSubmit = async () => {
        if (hasSubmitted) return;
        setHasSubmitted(true);
        const pickData = {
            userId: myUserId,
            masterPoolId: masterPoolId,
            index: Number(pickIndex),
            bracketPicks: bracketState,
            seriesLengths: seriesLengths,
        }
        //console.log("PICK DATA", pickData);

        try {
            await saveSuperPickemBracketPicks(pickData, {
                onSuccess: (response) => {
                    //console.log("Picks submitted successfully: ", response);
                    setSubmissionData(response);
                    setSubmissionModalOpen(true);
                },
                onError: (error) => {
                    console.error("Error submitting picks: ", error);
                    if (error.response.status === 401) {
                        dispatch(userLogoutAction());
                    } else {
                        setSubmissionData({ error: "An error occurred while submitting your picks." });
                        setSubmissionModalOpen(true);
                    }
                }
            });
        } catch (error) {
            console.error("Error submitting picks: ", error);
            setSubmissionData({ error: "An error occurred while submitting your picks." });
            setSubmissionModalOpen(true);
        }
    };

    const handleCancel = () => {
        setBracketState(originalBracketPicks);
        setSeriesLengths(originalSeriesLengths);
    };

    const handleClearBracket = () => {
        setBracketState([]);
        setSeriesLengths([]);
    }

    const showCancelButton = useMemo(() => {
        return (
            (originalBracketPicks && originalBracketPicks.length > 0) ||
            (usingSeriesLengths && originalSeriesLengths && originalSeriesLengths.length > 0)
        );
    }, [originalBracketPicks, usingSeriesLengths, originalSeriesLengths]);

    const uriEncoded = (link) => {
        return encodeURI(`${encodeURIComponent(link)}`);
    }

    useEffect(() => {
        if (!isUserInThisPool) {
            navigateToTop(`/${uriEncoded(gameAddress)}?mId=${masterPoolId}&gId=${gameId}&page=home`);
        }
    }, [isUserInThisPool, gameAddress, masterPoolId, gameId, navigateToTop]);

    return (
        <>
            {myBracketModalInfo && (
                <>
                    <QuickPickGamesComing
                        page="my-bracket"
                        title={myBracketModalInfo.title}
                        body={myBracketModalInfo.body}
                        quickPick={false}
                    />
                    
                    <Box sx={{ marginBottom: '48px' }} />
                    
                </>
            )}

            {!myBracketModalInfo && (
                <>
                    {countdownText && (
                        <CountdownClock
                            text={countdownText}
                            targetTimeISO={countdownTime}
                            countdownTextColor={countdownInfo?.textColor}
                            countdownBackgroundColor={countdownInfo?.backgroundColor}
                        />
                    )}
                    <Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: '32px',
                                    fontWeight: '700',
                                    marginTop: '16px',
                                    marginBottom: '16px',
                                }}
                            >
                                {page === 'my-bracket' ? 'My Bracket' : `${selectedUserBracketInfo?.username}'s Bracket`}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '16px',
                                marginTop: '16px',
                                marginBottom: '16px',
                                marginLeft: '74px',
                                marginRight: '74px',
                                maxWidth: '1300px',
                                margin: '0 auto',
                                paddingBottom: '48px',
                            }}
                        >
                            <Box>
                                <Typography sx={{ fontWeight: '700' }}>{picksInfo[0]?.name}</Typography>
                                <Typography sx={{ fontWeight: '700' }}>
                                    {!hasBracketDeadlinePassed
                                        ? `${picksInfo[0]?.points || 0} Point${(picksInfo[0]?.points || 0) === 1 ? '' : 's'}`
                                        : `${desktopPointsLayout[0]?.points || 0} Point${(desktopPointsLayout[0]?.points || 0) === 1 ? '' : 's'}`
                                    }
                                </Typography>
                            </Box>

                            <Box>
                                <Typography sx={{ fontWeight: '700' }}>{picksInfo[1]?.name}</Typography>
                                <Typography sx={{ fontWeight: '700' }}>
                                    {!hasBracketDeadlinePassed
                                        ? `${picksInfo[1]?.points || 0} Point${(picksInfo[1]?.points || 0) === 1 ? '' : 's'}`
                                        : `${desktopPointsLayout[1]?.points || 0} Point${(desktopPointsLayout[1]?.points || 0) === 1 ? '' : 's'}`
                                    }
                                </Typography>
                            </Box>

                            <Box>
                                <Typography sx={{ fontWeight: '700' }}>{picksInfo[2]?.name}</Typography>
                                <Typography sx={{ fontWeight: '700' }}>
                                    {!hasBracketDeadlinePassed
                                        ? `${picksInfo[2]?.points || 0} Point${(picksInfo[2]?.points || 0) === 1 ? '' : 's'}`
                                        : `${desktopPointsLayout[2]?.points || 0} Point${(desktopPointsLayout[2]?.points || 0) === 1 ? '' : 's'}`
                                    }
                                </Typography>
                            </Box>

                            <Box>
                                <Typography sx={{ fontWeight: '700' }}>{picksInfo[3]?.name}</Typography>
                                <Typography sx={{ fontWeight: '700' }}>
                                    {!hasBracketDeadlinePassed
                                        ? `${picksInfo[3]?.points || 0} Point${(picksInfo[3]?.points || 0) === 1 ? '' : 's'}`
                                        : `${desktopPointsLayout[3]?.points || 0} Point${(desktopPointsLayout[3]?.points || 0) === 1 ? '' : 's'}`
                                    }
                                </Typography>
                            </Box>

                            <Box>
                                <Typography sx={{ fontWeight: '700' }}>{picksInfo[2]?.name}</Typography>
                                <Typography sx={{ fontWeight: '700' }}>
                                    {!hasBracketDeadlinePassed
                                        ? `${picksInfo[2]?.points || 0} Point${(picksInfo[2]?.points || 0) === 1 ? '' : 's'}`
                                        : `${desktopPointsLayout[4]?.points || 0} Point${(desktopPointsLayout[4]?.points || 0) === 1 ? '' : 's'}`
                                    }
                                </Typography>
                            </Box>

                            <Box>
                                <Typography sx={{ fontWeight: '700' }}>{picksInfo[1]?.name}</Typography>
                                <Typography sx={{ fontWeight: '700' }}>
                                    {!hasBracketDeadlinePassed
                                        ? `${picksInfo[1]?.points || 0} Point${(picksInfo[1]?.points || 0) === 1 ? '' : 's'}`
                                        : `${desktopPointsLayout[5]?.points || 0} Point${(desktopPointsLayout[5]?.points || 0) === 1 ? '' : 's'}`
                                    }
                                </Typography>
                            </Box>

                            <Box>
                                <Typography sx={{ fontWeight: '700' }}>{picksInfo[0]?.name}</Typography>
                                <Typography sx={{ fontWeight: '700' }}>
                                    {!hasBracketDeadlinePassed
                                        ? `${picksInfo[0]?.points || 0} Point${(picksInfo[0]?.points || 0) === 1 ? '' : 's'}`
                                        : `${desktopPointsLayout[6]?.points || 0} Point${(desktopPointsLayout[6]?.points || 0) === 1 ? '' : 's'}`
                                    }
                                </Typography>
                            </Box>
                        </Box>
                        
                                <Box sx={{ position: 'relative' }}>
                                    <AgnosticSixteenTeamBracket
                                        games={tournamentGames}
                                        teams={teams}
                                        league={league}
                                        handleGameClick={handleGameClick}
                                        setRegionCompleteModalOpen={setRegionCompleteModalOpen}
                                        showDesktopNavBar={false}
                                        bracketState={page === 'my-bracket' ? bracketState : selectedUserBracketInfo?.bracketPicks || []}
                                        seriesLengths={page === 'my-bracket' ? seriesLengths : selectedUserBracketInfo?.seriesLengths || []}
                                        setSeriesLengths={setSeriesLengths}
                                        lastRound={lastRound}
                                        picksInfo={picksInfo}
                                        hasBracketDeadlinePassed={hasBracketDeadlinePassed}
                                    />
                                </Box>

                    </Box>
                    <Box
                        sx={{
                            marginTop: '-1197.5px',
                            marginLeft: '74px',
                        }}
                    >
                        <AgnosticDesktopChamp
                            matchupId={15}
                            league={league}
                            teams={teams}
                            round={4}
                            lastRound={lastRound}
                            handleGameClick={handleGameClick}
                            games={tournamentGames}
                            bracketState={page === 'my-bracket' ? bracketState : selectedUserBracketInfo?.bracketPicks || []}
                            seriesLengths={page === 'my-bracket' ? seriesLengths : selectedUserBracketInfo?.seriesLengths || []}
                            setSeriesLengths={setSeriesLengths}
                            usingSeriesLengths={usingSeriesLengths}
                            hasBracketDeadlinePassed={hasBracketDeadlinePassed}

                        />
                    </Box>
                    <Box>
                        <Box sx={{ position: 'relative' }}>

                            {submitBarOpen && (
                                <UserSubmitBar
                                    picksToMake={picksToMake}
                                    tiebreakerCount={tiebreakerCount}
                                    handleSubmit={handleBracketSubmit}
                                    handleCancel={handleCancel}
                                    hasSubmitted={hasSubmitted}
                                    handleClear={handleClearBracket}
                                    showCancelButton={showCancelButton}
                                    usingBracketState={true}
                                    usingSeriesLengths={usingSeriesLengths}
                                />
                            )}

                            {submissionModalOpen && (
                                <SuperPickemBracketSubmissionModal
                                    modalOpen={submissionModalOpen}
                                    setModalOpen={setSubmissionModalOpen}
                                    submissionData={submissionData}
                                />
                            )}
                        </Box>
                </Box>
            </>
        )}
    </>
    );
};

export default SixteenTeamDesktopBracket;